import * as React from "react";
import * as ReactRedux from "react-redux";

import TextField from "material-ui/TextField";
import Dialog from "material-ui/Dialog";
import RaisedButton from "material-ui/RaisedButton";
import FlatButton from "material-ui/FlatButton";
import * as Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css";
import CloseIcon from "material-ui/svg-icons/navigation/close";

import LabExampleSwitcher from "./LabExampleSwitcher";
import LabUserSwitcher from "./LabUserSwitcher";
import LabContentHttp from "./LabContentHttp";
import LabContentJs from "./LabContentJs";

import FullscreenIcon from "./Lab/FullscreenIcon";

import "codemirror/lib/codemirror.css";
import "../styles/resizer.css";

import {
	selectors,
	actions,
	AuthedUser,
	AuthedUserDialog,
	LabExample,
	LabExampleJs,
} from "../reducers/lab";

import { selectors as userSelectors, AppIdInfo } from "../reducers/current_user";

interface LabHeaderProps {
	docTitle: string;
	heading: string;

	isFullscreen: boolean;

	closeLab(): any;

	toggleFullscreen(): any;
}

const LabHeader = (props: LabHeaderProps) => (
	<div>
		<div
			style={{
				height: "60px",
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "space-between",
			}}
		>
			<div
				style={{
					display: "inline-flex",
					alignItems: "center",
				}}
			>
				{props.isFullscreen && (
					<img
						src={require("../res/blockv_icon_only_grayscale_light.svg")}
						style={{
							height: "70px",
							marginBottom: 0,
							marginTop: -5,
						}}
					/>
				)}

				<div
					style={{
						alignItems: "baseline",
						display: "inline-flex",
					}}
				>
					<h3
						style={{
							margin: 0,
							fontWeight: 700,
							color: "#66D9EF",
							paddingLeft: !props.isFullscreen ? "1rem" : 0,
						}}
					>
						LAB
					</h3>

					<h3
						style={{
							fontFamily: "Rajdhani",
							color: "#D8D8D8",
							margin: 0,
							textTransform: "uppercase",
							marginLeft: "0.5rem",
							fontWeight: 100,
						}}
					>
						{props.docTitle} /
					</h3>

					<p
						style={{
							color: "white",
							margin: 0,
							marginLeft: "0.5rem",
							fontWeight: 500,
						}}
					>
						{props.heading}
					</p>
				</div>
			</div>

			<div style={{ display: "inline-flex" }}>
				<Tooltip
					placement="bottomRight"
					overlay={<span>{props.isFullscreen ? "Close" : "Open"} Fullscreen</span>}
				>
					<FullscreenIcon
						isFullscreen={props.isFullscreen}
						color="white"
						hoverColor="#66D9EF"
						style={{
							cursor: "pointer",
							margin: "auto 0 auto 0.5rem",
						}}
						onClick={props.toggleFullscreen}
					/>
				</Tooltip>

				<Tooltip placement="bottomRight" overlay={<span>Close Lab</span>}>
					<CloseIcon
						color="white"
						hoverColor="#66D9EF"
						style={{
							cursor: "pointer",
							margin: "auto 1rem auto 0.5rem",
						}}
						onClick={props.closeLab.bind(this)}
					/>
				</Tooltip>
			</div>
		</div>
	</div>
);

interface LabProps {
	example: LabExample;
	title: string;
	heading: string;
	authedUserDialog: AuthedUserDialog;
	isFullscreen: boolean;
	authedUser: AuthedUser;

	style?: React.CSSProperties;

	closeLab(): any;

	resetLabRequest(): any;

	performRequest(): any;

	closeAuthedUserDialog(user: AuthedUser): any;

	toggleFullscreen(): any;

	apps: AppIdInfo[];
}

class Lab extends React.Component<LabProps, any> {
	state = {
		isUserPatchDialogOpen: false,
		isUserDeleteDialogOpen: false,
		isAdminDeleteDialogOpen: false,
		adminDeleteModalConfirmText: "",
	};

	// Label should be firstName lastName, or show the ID if they have no name set
	get authedUserFriendlyName(): string {
		const { authedUser } = this.props;

		if (authedUser) {
			if (authedUser.firstName !== undefined && authedUser.lastName !== undefined) {
				return `${authedUser.firstName} ${authedUser.lastName}`;
			}
		}

		return "this user";
	}

	// Label should be firstName lastName, or show the ID if they have no name set
	get dialogAuthedUserFriendlyName(): string {
		const { authedUser } = this.props.authedUserDialog;

		if (authedUser) {
			if (authedUser.firstName !== undefined && authedUser.lastName !== undefined) {
				return `${authedUser.firstName} ${authedUser.lastName}`;
			}
		}

		return "this user";
	}

	// called when the user clicks the perform request button and
	// useful for intercepting requests before they are made for
	// validation or warning purposes
	attemptToPerformRequest() {
		const { example } = this.props;

		console.log(example);
		if (example.type !== "http") {
			throw new Error(`cannot perform request of type "${example.type}" (only http supported)`);
		}

		const httpMethod: string = example.data.request.method;

		// check if its a delete user request
		if (httpMethod === "DELETE" && example.data.request.url.endsWith("/v1/user")) {
			if (this.props.authedUser.isAdmin) {
				this.setState({
					isAdminDeleteDialogOpen: true,
				});
			} else {
				this.setState({
					isUserDeleteDialogOpen: true,
				});
			}

			return;
		}

		// check if its a patch user request
		if (httpMethod === "PATCH" && example.data.request.url.endsWith("/v1/user")) {
			try {
				const body = JSON.parse(example.data.request.body);

				if (typeof body.password !== "undefined") {
					this.setState({
						isUserPatchDialogOpen: true,
					});

					return;
				}
			} catch (e) {
				// let the request go through if the body cannot be parsed
			}
		}

		// perform the request if it was not stopped
		this.props.performRequest();
	}

	// called when the user confirms a warning modal
	// validation of modal input can be done here if necessary
	confirmPerformRequest(e: Event) {
		e.preventDefault();

		if (this.state.isUserPatchDialogOpen) {
			this.closeUserPatchDialog();
		} else if (this.state.isUserDeleteDialogOpen) {
			this.closeUserDeleteDialog();
		} else if (this.state.isAdminDeleteDialogOpen) {
			if (this.state.adminDeleteModalConfirmText !== "DELETE") {
				// do not allow the request to go through
				return;
			}

			this.closeAdminDeleteDialog();
		}

		// perform the request if all validation passes
		this.props.performRequest();
	}

	closeUserPatchDialog() {
		this.setState({
			isUserPatchDialogOpen: false,
		});
	}

	closeUserDeleteDialog() {
		this.setState({
			isUserDeleteDialogOpen: false,
		});
	}

	closeAdminDeleteDialog() {
		this.setState({
			isAdminDeleteDialogOpen: false,
			adminDeleteModalConfirmText: "",
		});
	}

	renderLabContent() {
		const { example, isFullscreen } = this.props;
		const { resetLabRequest } = this.props;

		if (example.type === "http") {
			return (
				<LabContentHttp
					example={example}
					isFullscreen={isFullscreen}
					attemptToPerformRequest={this.attemptToPerformRequest.bind(this)}
					// todo: move into own reducer with mapStateToProps
					requestBodyIsAllowed={example.data.request.isBodyAllowed}
					resetLabRequest={resetLabRequest}
				/>
			);
		}

		if (example.type === "js") {
			return <LabContentJs example={example as LabExampleJs} isFullscreen={isFullscreen} />;
		}
	}

	render() {
		const {
			title,
			heading,
			authedUserDialog,
			isFullscreen,
			style,
			closeAuthedUserDialog,
			closeLab,
			toggleFullscreen,
			example,
		} = this.props;

		return (
			<div style={style}>
				<Dialog
					title="New User Authenticated Successfully"
					contentStyle={{ width: "100%", maxWidth: "500px" }}
					actions={
						<>
							<small style={{ position: "absolute", left: "1.61rem", lineHeight: "36px" }}>
								<em>Note:</em> Your password will not be stored.
							</small>
							<FlatButton
								label="Not Now"
								primary={false}
								onClick={() => closeAuthedUserDialog(null)}
							/>
							<RaisedButton
								label="Switch"
								primary={true}
								onClick={() => closeAuthedUserDialog(authedUserDialog.authedUser)}
							/>
						</>
					}
					modal={true}
					open={authedUserDialog.isOpen}
				>
					<p style={{ margin: "0 0 1.61rem 0" }}>
						You can now use this authenticated user for subsequent API requests. You can always
						switch between users using the <em>Authenticated As</em> drop down in the Try-It Lab.
					</p>
					<p style={{ margin: "0 0 1.61rem 0" }}>
						Do you want to switch to <strong>{this.dialogAuthedUserFriendlyName}</strong> for future
						requests?
					</p>
				</Dialog>

				<Dialog
					title="Warning: You are about to update the password"
					open={this.state.isUserPatchDialogOpen}
					onRequestClose={this.closeUserPatchDialog.bind(this)}
					actions={
						<>
							<FlatButton label="Cancel" onClick={this.closeUserPatchDialog.bind(this)} />
							<RaisedButton
								label="PATCH"
								primary={true}
								style={{ marginLeft: "0.5rem" }}
								onClick={this.confirmPerformRequest.bind(this)}
							/>
						</>
					}
				>
					<p>
						Please make note of the changed password for {this.authedUserFriendlyName}. Would you
						like to proceed?
					</p>
				</Dialog>

				<Dialog
					title="Warning: You are about to delete an admin user"
					open={this.state.isAdminDeleteDialogOpen}
					onRequestClose={this.closeAdminDeleteDialog.bind(this)}
					actions={
						<>
							<FlatButton label="Cancel" onClick={this.closeAdminDeleteDialog.bind(this)} />
							<RaisedButton
								label="DELETE"
								primary={true}
								style={{ marginLeft: "0.5rem" }}
								disabled={this.state.adminDeleteModalConfirmText !== "DELETE"}
								onClick={this.confirmPerformRequest.bind(this)}
							/>
						</>
					}
				>
					<p>Please type "DELETE" to permanently delete {this.authedUserFriendlyName}.</p>

					<form
						style={{
							marginLeft: "1.61rem",
							marginRight: "1.61rem",
						}}
						onSubmit={this.confirmPerformRequest.bind(this)}
					>
						<TextField
							hintText="DELETE"
							fullWidth
							autoFocus
							value={this.state.adminDeleteModalConfirmText}
							onChange={(e, value) => this.setState({ adminDeleteModalConfirmText: value })}
						/>
					</form>
				</Dialog>

				<Dialog
					title="Warning: You are about to delete a user"
					open={this.state.isUserDeleteDialogOpen}
					onRequestClose={this.closeUserDeleteDialog.bind(this)}
					actions={
						<>
							<FlatButton label="Cancel" onClick={this.closeUserDeleteDialog.bind(this)} />
							<RaisedButton
								label="DELETE"
								primary={true}
								style={{ marginLeft: "0.5rem" }}
								onClick={this.confirmPerformRequest.bind(this)}
							/>
						</>
					}
				>
					<p>Please click "DELETE" to permanently delete {this.authedUserFriendlyName}.</p>
				</Dialog>

				<LabHeader
					docTitle={title}
					heading={heading}
					isFullscreen={isFullscreen}
					closeLab={closeLab}
					toggleFullscreen={toggleFullscreen}
				/>

				<div>
					<div
						style={{
							height: "48px",
							backgroundColor: "white",
							paddingLeft: "calc(1rem - 3px)",
							paddingRight: "1rem",
							display: "flex",
							borderLeft: "3px solid #445a64",
						}}
					>
						<LabExampleSwitcher examplesType={example.type} />
						<LabUserSwitcher />
					</div>
				</div>

				{this.renderLabContent()}
			</div>
		);
	}
}

const mapStateToProps = state => ({
	isFullscreen: selectors.selectIsFullscreen(state),
	apps: userSelectors.selectApps(state),
	example: selectors.selectExample(state),
	title: selectors.selectTitle(state),
	heading: selectors.selectHeading(state),
	authedUserDialog: selectors.selectAuthedUserDialog(state),
	authedUser: selectors.selectAuthedUser(state),
});

const mapDispatchToProps = {
	toggleFullscreen: actions.toggleFullscreen,
	closeLab: actions.close,
	resetLabRequest: actions.resetLabRequest,
	performRequest: actions.performRequest,
	closeAuthedUserDialog: actions.closeAuthedUserDialog,
};

export default ReactRedux.connect(
	mapStateToProps,
	mapDispatchToProps
)(Lab);



// WEBPACK FOOTER //
// ./src/components/Lab.tsx